import { forwardRef } from "react";
import { motion } from "framer-motion";
import styled, { css } from "styled-components";

const variantStyles = {
  primary: css`
    background: ${({ theme }) => theme.colors.red};
    color: ${({ theme }) => theme.colors.white};
    box-shadow: ${({ theme }) => theme.shadows.soft};

    &:hover {
      background: ${({ theme }) => theme.colors.redHover};
    }
  `,
  secondary: css`
    background: rgba(255, 255, 255, 0.14);
    color: ${({ theme }) => theme.colors.white};
    border: 1px solid rgba(255, 255, 255, 0.12);

    &:hover {
      background: rgba(255, 255, 255, 0.22);
    }
  `,
  ghost: css`
    background: transparent;
    color: ${({ theme }) => theme.colors.text};
    border: 1px solid ${({ theme }) => theme.colors.border};

    &:hover {
      background: rgba(255, 255, 255, 0.08);
    }
  `,
  danger: css`
    background: rgba(229, 9, 20, 0.18);
    color: ${({ theme }) => theme.colors.white};
    border: 1px solid rgba(229, 9, 20, 0.32);

    &:hover {
      background: rgba(229, 9, 20, 0.3);
    }
  `,
};

const sizeStyles = {
  sm: css`
    min-height: 40px;
    padding: 0.65rem 1rem;
    font-size: 0.92rem;
  `,
  md: css`
    min-height: 48px;
    padding: 0.85rem 1.2rem;
    font-size: 0.98rem;
  `,
  lg: css`
    min-height: 54px;
    padding: 0.95rem 1.45rem;
    font-size: 1rem;
  `,
};

const StyledButton = styled(motion.button)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  width: ${({ $fullWidth }) => ($fullWidth ? "100%" : "fit-content")};
  border-radius: ${({ theme }) => theme.radii.pill};
  font-weight: 700;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease, border-color 0.2s ease;
  ${({ $variant }) => variantStyles[$variant] || variantStyles.primary};
  ${({ $size }) => sizeStyles[$size] || sizeStyles.md};

  &:focus-visible {
    outline: 3px solid rgba(255, 255, 255, 0.25);
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const IconSlot = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 1.05em;
`;

const Button = forwardRef(
  (
    {
      children,
      icon,
      trailingIcon,
      variant = "primary",
      size = "md",
      fullWidth = false,
      as,
      ...props
    },
    ref
  ) => {
    const motionProps = as ? {} : { whileTap: { scale: 0.98 } };

    return (
      <StyledButton
        ref={ref}
        as={as}
        $variant={variant}
        $size={size}
        $fullWidth={fullWidth}
        {...motionProps}
        {...props}
      >
        {icon ? <IconSlot aria-hidden>{icon}</IconSlot> : null}
        <span>{children}</span>
        {trailingIcon ? <IconSlot aria-hidden>{trailingIcon}</IconSlot> : null}
      </StyledButton>
    );
  }
);

Button.displayName = "Button";

export default Button;
